import { useState } from 'react';
import { CheckCircle2, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DataErrorBanner, type DataErrorDetail } from '@/components/ui/data-error-banner';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatDateTime, type DepotContainerVisit, type DepotMoveOrder, useDepotList } from './depot-hooks';
import {
  DepotEmptyRow,
  DepotGridPanel,
  depotTableClassName,
  depotTableHeadClassName,
  depotTdClassName,
  depotThClassName,
} from './depot-grid';

type MoveFormState = {
  visitId: string;
  depotId: string;
  moveType: 'shift' | 'reposition' | 'lift_on' | 'lift_off' | 'load_to_truck';
  fromSlotCode: string;
  toSlotCode: string;
  assignedTo: string;
  equipmentRef: string;
};

const EMPTY_MOVE: MoveFormState = {
  visitId: '',
  depotId: '',
  moveType: 'shift',
  fromSlotCode: '',
  toSlotCode: '',
  assignedTo: '',
  equipmentRef: '',
};

export default function DepotMovesPage() {
  const { items, loading, error, refresh } = useDepotList<DepotMoveOrder>('/depot/moves?limit=100');
  const { items: visits } = useDepotList<DepotContainerVisit>('/depot/containers?limit=100');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<MoveFormState>(EMPTY_MOVE);
  const [saving, setSaving] = useState(false);
  const [completingId, setCompletingId] = useState<string | null>(null);

  const pageError: DataErrorDetail | null = error
    ? { endpoint: '/depot/moves', message: error, status: 500, code: 'DEPOT_MOVES_LOAD_FAILED' }
    : null;

  const handleCreate = async () => {
    if (!form.visitId || !form.depotId) {
      toast.error('Visit and depot are required');
      return;
    }
    setSaving(true);
    try {
      await api.post('/depot/moves', {
        visitId: form.visitId,
        depotId: form.depotId,
        moveType: form.moveType,
        fromSlotCode: form.fromSlotCode.trim() || undefined,
        toSlotCode: form.toSlotCode.trim() || undefined,
        assignedTo: form.assignedTo.trim() || undefined,
        equipmentRef: form.equipmentRef.trim() || undefined,
      });
      toast.success('Move created');
      setDialogOpen(false);
      setForm(EMPTY_MOVE);
      await refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create move');
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async (moveId: string) => {
    setCompletingId(moveId);
    try {
      await api.post(`/depot/moves/${moveId}/complete`, {});
      toast.success('Move completed');
      await refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to complete move');
    } finally {
      setCompletingId(null);
    }
  };

  const activeVisits = visits.filter((visit) => visit.visit_status !== 'completed');

  return (
    <div className="space-y-5">
      {pageError && <DataErrorBanner error={pageError} entityLabel="Depot Moves" onRetry={refresh} />}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Move Desk</h2>
          <p className="text-sm text-muted-foreground">
            Create shift/reposition tasks and complete them to update yard state.
          </p>
        </div>
        <Button onClick={() => setDialogOpen(true)}>
          <Plus className="mr-1.5 h-4 w-4" />
          New Move
        </Button>
      </div>

      <DepotGridPanel title="Move Orders" subtitle="Yard movement queue in one editable worklist shape.">
        <table className={depotTableClassName}>
          <thead className={depotTableHeadClassName}>
            <tr>
              <th className={depotThClassName}>Move</th>
              <th className={depotThClassName}>Container</th>
              <th className={depotThClassName}>Type</th>
              <th className={depotThClassName}>From</th>
              <th className={depotThClassName}>To</th>
              <th className={depotThClassName}>Assigned / Equipment</th>
              <th className={depotThClassName}>Requested</th>
              <th className={depotThClassName}>Action</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading ? (
              <DepotEmptyRow colSpan={8}>Loading move orders...</DepotEmptyRow>
            ) : items.length === 0 ? (
              <DepotEmptyRow colSpan={8}>No move orders yet.</DepotEmptyRow>
            ) : (
              items.map((item) => (
                <tr key={item.id} className="hover:bg-muted/30">
                  <td className={`${depotTdClassName} font-mono font-semibold`}>{item.move_no}</td>
                  <td className={depotTdClassName}>{item.container_no}</td>
                  <td className={depotTdClassName}>{item.move_type}</td>
                  <td className={depotTdClassName}>{item.from_slot_code ?? '—'}</td>
                  <td className={depotTdClassName}>{item.to_slot_code ?? '—'}</td>
                  <td className={depotTdClassName}>{item.assigned_to ?? 'Unassigned'} · {item.equipment_ref ?? 'Equipment TBC'}</td>
                  <td className={`${depotTdClassName} text-xs text-muted-foreground`}>{formatDateTime(item.requested_at)}</td>
                  <td className={depotTdClassName}>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant={item.task_status === 'completed' ? 'outline' : 'secondary'}>{item.task_status}</Badge>
                      {item.task_status !== 'completed' && (
                        <Button size="sm" variant="outline" onClick={() => handleComplete(item.id)} disabled={completingId === item.id}>
                          {completingId === item.id ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1.5 h-4 w-4" />}
                          Complete
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </DepotGridPanel>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Move</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <Select
              value={form.visitId}
              onValueChange={(value) => {
                const selected = activeVisits.find((visit) => visit.id === value);
                setForm((current) => ({
                  ...current,
                  visitId: value,
                  depotId: selected?.depot_id ?? current.depotId,
                  fromSlotCode: selected?.current_slot_code ?? '',
                }));
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select container visit" />
              </SelectTrigger>
              <SelectContent>
                {activeVisits.map((visit) => (
                  <SelectItem key={visit.id} value={visit.id}>{visit.container_no} · {visit.visit_ref}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={form.moveType} onValueChange={(value: MoveFormState['moveType']) => setForm((current) => ({ ...current, moveType: value }))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="shift">Shift</SelectItem>
                <SelectItem value="reposition">Reposition</SelectItem>
                <SelectItem value="lift_on">Lift On</SelectItem>
                <SelectItem value="lift_off">Lift Off</SelectItem>
                <SelectItem value="load_to_truck">Load To Truck</SelectItem>
              </SelectContent>
            </Select>
            <Input placeholder="From Slot" value={form.fromSlotCode} onChange={(event) => setForm((current) => ({ ...current, fromSlotCode: event.target.value }))} />
            <Input placeholder="To Slot" value={form.toSlotCode} onChange={(event) => setForm((current) => ({ ...current, toSlotCode: event.target.value }))} />
            <Input placeholder="Assigned To" value={form.assignedTo} onChange={(event) => setForm((current) => ({ ...current, assignedTo: event.target.value }))} />
            <Input placeholder="Equipment Ref" value={form.equipmentRef} onChange={(event) => setForm((current) => ({ ...current, equipmentRef: event.target.value }))} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving}>
              {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Plus className="mr-1.5 h-4 w-4" />}
              Create Move
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
